import { SPECIAL_BEER_DUTY_OPTIONS } from "../../../shared/domain/schedule";
import type {
  ScheduleField,
  ScheduleTimeSlot,
} from "../../../shared/domain/schedule";
import { SCHEDULE_FIELDS, SCHEDULE_TIME_SLOTS } from "../../domain/schedule";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { currentScheduleDate } from "./scheduleEditor";
import type { GameEditorValue } from "./scheduleEditor";
import type { ActiveSchedulePlayer } from "./scheduleTypes";

export function GameEditorFields({
  value,
  players,
  activeLocked,
  opponentLocked,
  onChange,
}: {
  value: GameEditorValue;
  players: ActiveSchedulePlayer[];
  activeLocked: boolean;
  opponentLocked: boolean;
  onChange: (value: GameEditorValue) => void;
}) {
  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2">
        <DateField
          value={value.scheduledDate}
          onChange={(scheduledDate) => onChange({ ...value, scheduledDate })}
        />
        <TimeField
          value={value.timeSlot}
          onChange={(timeSlot) => onChange({ ...value, timeSlot })}
        />
      </div>
      <label className="grid gap-1 text-sm font-bold">
        OPPONENT
        <Input
          aria-label="Opponent"
          value={value.opponentName}
          onChange={(event) =>
            onChange({ ...value, opponentName: event.target.value })
          }
          placeholder="Opponent"
          disabled={opponentLocked}
          required
        />
      </label>
      <FieldSelect
        value={value.field}
        disabled={activeLocked}
        onChange={(field) => onChange({ ...value, field })}
      />
      <HomeAway
        value={value.isHome}
        disabled={activeLocked}
        onChange={(isHome) => onChange({ ...value, isHome })}
      />
      <BeerDutySelect
        value={value.beerDuty}
        players={players}
        disabled={activeLocked}
        onChange={(beerDuty) => onChange({ ...value, beerDuty })}
      />
    </>
  );
}

export function SeriesGameFields({
  gameNumber,
  value,
  players,
  onChange,
}: {
  gameNumber: number;
  value: GameEditorValue;
  players: ActiveSchedulePlayer[];
  onChange: (value: GameEditorValue) => void;
}) {
  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2">
        <DateField
          label={`Game ${gameNumber} date`}
          value={value.scheduledDate}
          onChange={(scheduledDate) => onChange({ ...value, scheduledDate })}
        />
        <TimeField
          label={`Game ${gameNumber} time`}
          value={value.timeSlot}
          onChange={(timeSlot) => onChange({ ...value, timeSlot })}
        />
      </div>
      <FieldSelect
        label={`Game ${gameNumber} field`}
        value={value.field}
        onChange={(field) => onChange({ ...value, field })}
      />
      <HomeAway
        label={`Game ${gameNumber} home or away`}
        value={value.isHome}
        onChange={(isHome) => onChange({ ...value, isHome })}
      />
      <BeerDutySelect
        label={`Game ${gameNumber} beer duty`}
        value={value.beerDuty}
        players={players}
        onChange={(beerDuty) => onChange({ ...value, beerDuty })}
      />
    </>
  );
}

function DateField({
  label = "Game date",
  value,
  onChange,
}: {
  label?: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="grid gap-1 text-sm font-bold">
      DATE
      <Input
        aria-label={label}
        type="date"
        min={currentScheduleDate()}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required
      />
    </label>
  );
}

function TimeField({
  label = "Game time",
  value,
  onChange,
}: {
  label?: string;
  value: ScheduleTimeSlot;
  onChange: (value: ScheduleTimeSlot) => void;
}) {
  return (
    <label className="grid gap-1 text-sm font-bold">
      TIME
      <select
        aria-label={label}
        className="h-10 rounded-[var(--radius-sm)] border border-border bg-background px-2 text-sm"
        value={value}
        onChange={(event) => onChange(event.target.value as ScheduleTimeSlot)}
      >
        {SCHEDULE_TIME_SLOTS.map((slot) => (
          <option key={slot.value} value={slot.value}>
            {slot.label} · {slot.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function FieldSelect({
  label = "Game field",
  value,
  disabled = false,
  onChange,
}: {
  label?: string;
  value: ScheduleField;
  disabled?: boolean;
  onChange: (value: ScheduleField) => void;
}) {
  return (
    <label className="grid gap-1 text-sm font-bold">
      FIELD
      <select
        aria-label={label}
        className="h-10 rounded-[var(--radius-sm)] border border-border bg-background px-2 text-sm"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value as ScheduleField)}
      >
        {SCHEDULE_FIELDS.map((field) => (
          <option key={field} value={field}>
            {field}
          </option>
        ))}
      </select>
    </label>
  );
}

function HomeAway({
  label = "Home or away",
  value,
  disabled = false,
  onChange,
}: {
  label?: string;
  value: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className="grid gap-1 text-sm font-bold">
      WE ARE
      <div className="flex gap-2" role="group" aria-label={label}>
        {([false, true] as const).map((isHome) => (
          <Button
            key={String(isHome)}
            type="button"
            size="sm"
            variant={value === isHome ? "selected" : "outline"}
            aria-pressed={value === isHome}
            disabled={disabled}
            onClick={() => onChange(isHome)}
            className="flex-1"
          >
            {isHome ? "HOME" : "AWAY"}
          </Button>
        ))}
      </div>
    </div>
  );
}

function BeerDutySelect({
  label = "Beer duty",
  value,
  players,
  disabled = false,
  onChange,
}: {
  label?: string;
  value: string;
  players: ActiveSchedulePlayer[];
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <label className="grid gap-1 text-sm font-bold">
      BEER DUTY
      <select
        aria-label={label}
        className="h-10 rounded-[var(--radius-sm)] border border-border bg-background px-2 text-sm"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      >
        {SPECIAL_BEER_DUTY_OPTIONS.map((option) => (
          <option key={option.kind} value={option.kind}>
            {option.editorLabel}
          </option>
        ))}
        {players.map((player) => (
          <option key={player.id} value={`player:${player.id}`}>
            {player.name}
          </option>
        ))}
      </select>
    </label>
  );
}
